import React, { useState, useEffect, useCallback } from "react";
import { Grid, GridColumn, GridNoRecords } from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog } from "@progress/kendo-react-dialogs";
import { Checkbox } from "@progress/kendo-react-inputs";
import { toast } from "react-toastify";
import { Tile } from "components/UIComponents/Tile";
import {
  DateFormatCell,
  AmountFormatCell,
  formatDateForAPI,
} from "components/common/formatter";
import {
  getMatterUndertakings,
  addUndertaking,
  updateUndertaking,
} from "api/index";
import styles from "./UndertakingsList.module.scss";
import { EditCommandCell } from "pages/Settings/GridComponents";
import { UndertakingForm } from "./UnderTakingForm";
import { GridLoader } from "components/GridLoader";

const UndertakingsList = ({
  matterId,
  clientId,
  refreshData,
  showInTabs = false,
}) => {
  const [undertakings, setUndertakings] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUndertaking, setSelectedUndertaking] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchUndertakings = useCallback(async () => {
    if (!matterId) {
      setUndertakings([]);
      return;
    }
    setLoading(true);
    try {
      const response = await getMatterUndertakings(matterId);
      setUndertakings(response.data || []);
    } catch (error) {
      console.error("Error fetching undertakings:", error);
    } finally {
      setLoading(false);
    }
  }, [matterId]);

  useEffect(() => {
    fetchUndertakings();
  }, [matterId, fetchUndertakings, refreshData]);

  const handleAdd = (e) => {
    e.stopPropagation();
    setSelectedUndertaking(null);
    setShowAddModal(true);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setSelectedUndertaking(null);
  };

  const handleEdit = (undertaking) => {
    setSelectedUndertaking(undertaking);
    setShowEditModal(true);
  };

  const handleSubmit = async (values) => {
    try {
      if (!values.description || values.description.trim() === "") {
        toast.error("Details is required");
        return;
      }

      const undertakingData = {
        clientId: clientId || 0,
        matterId: matterId || null,
        details: values.description.trim(),
        amount: values.amount || 0,
        date: formatDateForAPI(values.date),
        dateDischarge:
          values.dateDischarge && formatDateForAPI(values.dateDischarge),
        isFinancial: values.financial || false,
        active: true,
      };

      if (selectedUndertaking) {
        await updateUndertaking(
          selectedUndertaking.undertakingId,
          undertakingData
        );
        toast.success("Undertaking updated successfully");
      } else {
        await addUndertaking(undertakingData);
        toast.success("Undertaking added successfully");
      }

      await fetchUndertakings();
      handleCloseModal();
    } catch (error) {
      console.error("Error saving undertaking:", error);
      throw error;
    }
  };

  const gridContent = (
    <>
      <Grid
        data={undertakings}
        className={`fs-md pb-1 ${styles.gridheight}`}
        sortable
        resizable
        showLoader={loading}
        loader={
          <div
            style={{
              height: "500px",
            }}
          >
            <GridLoader />
          </div>
        }
      >
        <GridNoRecords>
          {loading ? (
            <span> </span>
          ) : (
            undertakings && undertakings.length === 0 && "No records available"
          )}
        </GridNoRecords>
        <GridColumn
          field="date"
          title="Date"
          width="120px"
          cell={DateFormatCell}
          className="gridBoldField ps-4"
          headerClassName="ps-4"
        />
        <GridColumn field="details" title="Description" />
        <GridColumn
          field="amount"
          title="Amount"
          width="120px"
          cell={AmountFormatCell}
          headerClassName="text-end"
        />
        <GridColumn
          field="dateDischarge"
          title="Date Discharge"
          width="140px"
          cell={DateFormatCell}
        />
        <GridColumn
          field="isFinancial"
          title="Financial"
          width="100px"
          cell={(props) => (
            <td className="text-center">
              <Checkbox checked={!!props.dataItem.isFinancial} readOnly />
            </td>
          )}
        />

        <GridColumn
          title="Action"
          width="80px"
          cell={(props) => (
            <EditCommandCell {...props} enterEdit={handleEdit} />
          )}
        />
      </Grid>
    </>
  );

  return (
    <div className={showInTabs ? "mt-2" : "container pt-3"}>
      {showInTabs ? (
        <>
          <div className="d-flex mb-2">
            <Button
              type="button"
              className="ms-auto button-small bgBtnSecondary fw-bold me-1"
              onClick={handleAdd}
              disabled={!matterId}
            >
              Add Undertaking
            </Button>
          </div>
          {gridContent}
        </>
      ) : (
        <Tile
          heading="Undertakings"
          className="mb-2 py-2 px-4 overflow-auto"
          height="580px"
          headingItems={
            <div>
              <Button
                type="button"
                className="button-small bgBtnSecondary fw-bold me-1"
                onClick={handleAdd}
              >
                Add Undertaking
              </Button>
            </div>
          }
        >
          {gridContent}
        </Tile>
      )}

      {showAddModal && (
        <Dialog title="Add Undertaking" onClose={handleCloseModal} width={500}>
          <UndertakingForm
            onCancel={handleCloseModal}
            onSubmit={handleSubmit}
            initialValues={{
              date: new Date(),
            }}
          />
        </Dialog>
      )}

      {showEditModal && (
        <Dialog title="Edit Undertaking" onClose={handleCloseModal} width={500}>
          <UndertakingForm
            onCancel={handleCloseModal}
            onSubmit={handleSubmit}
            initialValues={{
              description: selectedUndertaking?.details || "",
              amount: selectedUndertaking?.amount || 0,
              date: selectedUndertaking?.date
                ? new Date(selectedUndertaking.date)
                : null,
              dateDischarge: selectedUndertaking?.dateDischarge
                ? new Date(selectedUndertaking.dateDischarge)
                : null,
              financial: selectedUndertaking?.isFinancial || false,
            }}
          />
        </Dialog>
      )}
    </div>
  );
};

export default UndertakingsList;
